import type { VoicemailSettings } from '@super/shared-types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

interface VoicemailSettingsCardProps {
  value: VoicemailSettings
  onChange: (next: VoicemailSettings) => void
}

export function VoicemailSettingsCard({
  value,
  onChange,
}: VoicemailSettingsCardProps) {
  const leaveDisabled = !value.voicemailDetectionEnabled
  const messageDisabled = leaveDisabled || !value.leaveVoicemailEnabled

  return (
    <Card>
      <CardHeader>
        <CardTitle>Voicemail</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <Label className="ml-3 text-sm font-medium">Detect voicemail</Label>
            <p className="ml-3 text-xs text-muted-foreground">
              Classify the recipient line and fire the voicemail outcome instead
              of waiting out the briefing timeout.
            </p>
          </div>
          <Switch
            checked={value.voicemailDetectionEnabled}
            onCheckedChange={checked =>
              onChange({ ...value, voicemailDetectionEnabled: checked })
            }
          />
        </div>

        <div className="flex items-start justify-between gap-3">
          <div className={leaveDisabled ? 'space-y-1 opacity-50' : 'space-y-1'}>
            <Label className="ml-3 text-sm font-medium">Leave a message</Label>
            <p className="ml-3 text-xs text-muted-foreground">
              When a machine answers, play the message below at the beep. Off
              declines silently.
            </p>
          </div>
          <Switch
            checked={value.leaveVoicemailEnabled}
            disabled={leaveDisabled}
            onCheckedChange={checked =>
              onChange({ ...value, leaveVoicemailEnabled: checked })
            }
          />
        </div>

        <div className={messageDisabled ? 'space-y-1 opacity-50' : 'space-y-1'}>
          <Label
            htmlFor="voicemail-message"
            className="ml-3 text-sm font-medium"
          >
            Voicemail message
          </Label>
          <Textarea
            id="voicemail-message"
            placeholder="Hi, this is {agent_name} with {company_name}. It looks like now isn't the best time to connect, so please give us a callback whenever you're ready!"
            value={value.voicemailMessage ?? ''}
            disabled={messageDisabled}
            onChange={e =>
              onChange({
                ...value,
                voicemailMessage: e.target.value || null,
              })
            }
          />
        </div>
      </CardContent>
    </Card>
  )
}
